import { useState, useEffect } from 'react';
import classNames from 'classnames';
import NULL_PRAYER from 'assets/nullPrayer.png';
import NULL_STORIES from 'assets/nullStories.png';
import NULL_DEVOTIONALS from 'assets/nullDevotionals.png';
import NULL_SCHEDULED_MESSAGES from 'assets/nullScheduledMessages.png';
import NULL_ACTIONS from 'assets/nullActions.png';
import NULL_DATA from 'assets/nullData.png';
import NULL_DATA_2 from 'assets/nullData2.png';
import NULL_DEVO_MESSAGES from 'assets/nullDevoMessages.png';
import NULL_DISCOVER from 'assets/nullDiscover.png';
import NULL_SETTINGS from 'assets/nullSettings.svg';
import NOT_FOUND from 'assets/notFound.png';
import SHOPPING_BAG from 'assets/feather/shopping-bag.svg';
import CONVERSATION_PROMPTS from 'assets/nullStates/conversationPrompts.svg';
import ADMIN_CONVERSATION_PROMPTS from 'assets/nullStates/adminConversationPrompts.svg';
import Button from 'components/atoms/Button';
import Typography from 'components/atoms/Typography';
import { NullStateType } from 'utils/frontendTypes';

function NullState({
  type,
  size,
  title,
  text,
  hideImage,
  imageClassName,
  imageContainerClassName,
  createAction,
  createBtnText,
  className,
}: {
  type?: NullStateType;
  size?: 'small' | 'large';
  hideImage?: boolean;
  imageClassName?: string;
  imageContainerClassName?: string;
  createAction?: () => void;
  title?: string;
  text?: string;
  createBtnText?: string;
  className?: string;
}) {
  const [config, setConfig] = useState({ image: '', text: '', title: '' });

  const getNullConfig = () => {
    switch (type) {
      case 'prayer': {
        return {
          title: 'No prayer requests yet',
          text: 'People can text their requests in any time. Make sure to remind them of that.',
          image: NULL_PRAYER,
        };
      }
      case 'users': {
        return {
          title: 'No people yet',
          text: `To add people, navigate to Settings —> Integrations to set up an integration or upload a CSV file. New people can opt in by texting your account's phone number - keywords, prayer requests, even just "Hello!"`,
          image: NULL_PRAYER,
        };
      }
      case 'userPrayer': {
        return {
          title: 'No prayer requests yet',
          text: 'This user has not sent in any prayer requests yet',
          image: NULL_PRAYER,
        };
      }
      case 'stories': {
        return {
          title: 'Start receiving stories',
          text: 'Encourage your movement to send in stories by texting "I have a story".',
          image: NULL_STORIES,
        };
      }
      case 'userStories': {
        return {
          title: 'No stories submitted',
          text: 'This user has not submitted any stories.',
          image: NULL_STORIES,
        };
      }
      case 'series': {
        return {
          title: "You don't have any active series",
          text: 'Use series to help your people grow and engage with your content.',
          image: NULL_DEVOTIONALS,
        };
      }
      case 'userDevotionals': {
        return {
          title: 'No series interactions',
          text: 'This user has not subscribed to a series from your ministry.',
          image: NULL_DEVOTIONALS,
        };
      }
      case 'actions': {
        return {
          title: 'Set up a Keyword Automation',
          text: 'Keyword automations are keywords that you create that your people can then text to get a response.',
          image: NULL_ACTIONS,
        };
      }
      case 'userActions': {
        return {
          title: 'No interactions yet',
          text: 'This user has not interacted with any actions.',
          image: NULL_ACTIONS,
        };
      }
      case 'userActivity': {
        return {
          title: 'No interactions yet',
          text: 'There are no interactions for this timeframe.',
          image: NULL_SETTINGS,
        };
      }
      case 'noData': {
        return {
          title: "We don't have enough data yet!",
          text: 'We are still gathering data. Check back after some more people have used the platform.',
          image: NULL_DATA,
        };
      }
      case 'scheduledMessages': {
        return {
          title: 'No upcoming messages',
          text: 'Send messages to your entire congregation, groups, or individuals. You can send now or schedule for later. To start, click "Write a message."',
          image: NULL_SCHEDULED_MESSAGES,
          size: 'large',
        };
      }
      case 'scheduledMessagesDashboard': {
        return {
          title: 'No upcoming messages',
          text: 'Use the Broadcasts page to manage the messages that you send out',
          image: NULL_SCHEDULED_MESSAGES,
        };
      }
      case 'subscribers': {
        return {
          title: 'No Subscribers',
          text: 'Once someone subscribes to your series, they will show up here.',
          image: NULL_DATA_2,
        };
      }
      case 'surveys': {
        return {
          title: 'No Surveys',
          text: 'Create a survey to start collecting information from your people.',
          image: NULL_DATA_2,
        };
      }
      case 'devoMessages': {
        return {
          title: 'No Messages',
          text: 'You currently do not have any messages in your series. Click to add a message now!',
          image: NULL_DEVO_MESSAGES,
        };
      }
      case 'devoOnlyImmediateMessage': {
        return {
          title: 'No Series Messages',
          text: `You currently do not have any messages in your series that follow the initial subscribed message.
                Click to add a message now!`,
          image: NULL_DEVO_MESSAGES,
        };
      }
      case 'notFound': {
        return {
          title: "Oops! We couldn't find what you were looking for.",
          text: '',
          image: NOT_FOUND,
        };
      }
      case 'messageViewer': {
        return {
          title: 'No messages here yet',
          text: 'There are no messages to show at this time.',
          image: NULL_DEVO_MESSAGES,
        };
      }
      case 'messageViewerNotVerifiedExplorers': {
        return {
          title: 'Verify Your Account to Start Receiving Explorers',
          text: 'Click below to verify your account prior to receiving connections to Explorers.',
          image: NULL_DEVO_MESSAGES,
        };
      }
      case 'messageViewerNotVerifiedAttendees': {
        return {
          title: 'Verify Your Account to Start Texting People',
          text: 'Click below to verify your account prior to texting with people.',
          image: NULL_DEVO_MESSAGES,
        };
      }
      case 'messageSelect': {
        return {
          title: 'No messages have been selected',
          text: 'Select a message on the left and keep the conversation going!',
          image: NULL_DEVO_MESSAGES,
        };
      }
      case 'groups': {
        return {
          title: 'No Groups yet!',
          text: 'Create groups to view them here.',
          image: NULL_SETTINGS,
        };
      }
      case 'tags': {
        return {
          title: 'No Tags yet!',
          text: 'Create tags to help capture stories.',
          image: NULL_SETTINGS,
        };
      }
      case 'discover': {
        return {
          title: `Sorry, We Couldn't Find Any Matches`,
          text: 'Please try searching a different term',
          image: NULL_DISCOVER,
        };
      }
      case 'discoverSwimlane': {
        return {
          title: `No resources yet`,
          text: 'Check back later for more resources',
          image: NOT_FOUND,
        };
      }
      case 'inProgressChallengesList': {
        return {
          title: '',
          text: 'You completed all of your Challenges! When new challenges are available they will appear here.',
          image: NULL_ACTIONS,
        };
      }
      case 'completedChallengesList': {
        return {
          title: '',
          text: 'You have not completed any challenges. Toggle to In-Progress Challenges to begin.',
          image: NULL_ACTIONS,
        };
      }
      case 'inProgressChallenges': {
        return {
          title: 'All Current Challenges Completed!',
          text: 'As new Challenges are available, they will appear here. In the meantime, toggle to view your completed challenges',
          image: NULL_ACTIONS,
        };
      }
      case 'completedChallenges': {
        return {
          title: 'Complete your Challenges',
          text: `Complete your Challenge To-Do’s and claim your credits to officially complete your Challenge`,
          image: NULL_ACTIONS,
        };
      }
      case 'completedChallengesHome': {
        return {
          title: 'All Current Challenges Completed! 🎉',
          text: 'You completed all of your challenges! When new challenges are available they will appear here, and on the Challenges page. Click below to view your completed challenges.',
          image: undefined,
        };
      }
      case 'noSelectedChallengeHome': {
        return {
          title: `You do not have a favorite Challenge yet!`,
          text: 'Click below to view your challenges.',
          image: undefined,
        };
      }
      case 'products': {
        return {
          title: 'Get to know your people better!',
          text: 'The Gloo App is your key to powerful communication with both your existing people and new people to help them grow.',
          image: NULL_DATA,
        };
      }
      case 'favoritedKits': {
        return {
          title: "Let's get started",
          text: 'Click the heart icon on any Resource you Discover for it to appear here.',
          image: SHOPPING_BAG,
        };
      }
      case 'conversationPrompts': {
        return {
          title: 'No Results Found',
          text: 'We couldn’t find any results for that topic',
          image: CONVERSATION_PROMPTS,
        };
      }
      case 'adminConversationPrompts': {
        return {
          title: 'No Results Found',
          text: 'We couldn’t find any results, try a new search term.',
          image: ADMIN_CONVERSATION_PROMPTS,
        };
      }

      default: {
        return {
          title: 'Nothing here yet',
          text: 'There is nothing to show here.',
          image: NOT_FOUND,
        };
      }
    }
  };
  useEffect(() => {
    setConfig(getNullConfig());
  }, [type]);

  return (
    <div
      className={classNames(
        'my-2 flex flex-col max-w-lg text-center m-auto items-center justify-center animated fadeIn',
        className,
      )}
    >
      {!hideImage && (
        <div className={classNames('rounded-full', imageContainerClassName)}>
          <img
            src={config.image}
            alt="..."
            className={classNames('py-2 mb-8', imageClassName)}
            style={{ maxHeight: size === 'small' ? 140 : 190 }}
          />
        </div>
      )}
      <div className="flex flex-col items-center justify-center text-center gap-y-2">
        <Typography variant="h3">{title || config.title}</Typography>
        <Typography variant="base">{text || config.text}</Typography>
      </div>
      {createAction && (
        <Button
          type="primary"
          size="gloo-medium"
          dataDurableId="createActionButton"
          text={createBtnText || 'Create'}
          onClick={createAction}
          className="px-8"
        />
      )}
    </div>
  );
}

export default NullState;
